import { REDUCED_MOTION } from 'components/ui/tokens';
import { FreezeTemperature, TEMPERATURE } from './freezeUi';

// Motion recipes for the glacial flow. Each one is a plain sx object with its own
// keyframes and a reduced-motion opt-out, so nothing animates behind that setting.

/** Slow drift for the aurora blobs and the snowflake mark. */
export const freezeFloatSx = (durationMs = 5200) => ({
  animation: `freezeFloat ${durationMs}ms ease-in-out infinite alternate`,
  '@keyframes freezeFloat': {
    from: { transform: 'translate(0, 0) scale(1)' },
    to: { transform: 'translate(-22px, 18px) scale(1.2)' }
  },
  [REDUCED_MOTION]: { animation: 'none' }
});

/** A light sweep across a surface. The element must clip its own overflow. */
export const freezeSheenSx = () => ({
  position: 'relative' as const,
  overflow: 'hidden' as const,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: 'linear-gradient(105deg, transparent 32%, rgba(255,255,255,0.13) 50%, transparent 68%)',
    animation: 'freezeSheen 3800ms ease-in-out infinite alternate'
  },
  '@keyframes freezeSheen': {
    from: { transform: 'translateX(-58%)', opacity: 0.5 },
    to: { transform: 'translateX(58%)', opacity: 1 }
  },
  [REDUCED_MOTION]: { '&::after': { animation: 'none', opacity: 0 } }
});

/** A thin, bright edge that cuts across a surface, then rests: one pass every `periodMs`. The element
 *  must clip its own overflow. */
export const freezeGlintSx = (periodMs = 7000) => ({
  position: 'relative' as const,
  overflow: 'hidden' as const,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    background: 'linear-gradient(105deg, transparent 42%, rgba(255,255,255,0.95) 50%, transparent 58%)',
    transform: 'translateX(-120%)',
    animation: `freezeGlint ${periodMs}ms ease-in-out 1s infinite`
  },
  // The pass takes the first quarter of the period; the rest is the pause between cuts.
  '@keyframes freezeGlint': {
    '0%': { transform: 'translateX(-120%)' },
    '25%, 100%': { transform: 'translateX(120%)' }
  },
  [REDUCED_MOTION]: { '&::after': { animation: 'none', display: 'none' } }
});

/** Breathing halo. Carries the current choice and the countdown ring. */
export const freezeGlowRingSx = (temperature: FreezeTemperature = 'ice') => ({
  animation: 'freezeGlowRing 2800ms ease-in-out infinite alternate',
  '@keyframes freezeGlowRing': {
    from: { boxShadow: `0 0 40px -12px ${TEMPERATURE[temperature].glow}` },
    to: { boxShadow: `0 0 90px -6px ${TEMPERATURE[temperature].accent}55` }
  },
  [REDUCED_MOTION]: { animation: 'none' }
});

/** The gradient fill that never sits still: `backgroundSize` is oversized so the background moves
 *  inside the button, not the button. */
export const freezeLivingFillSx = (temperature: FreezeTemperature = 'ice') => {
  const tone = TEMPERATURE[temperature];
  const wash = temperature === 'ice' ? '#E6F7FF' : '#FFE6C7';
  return {
    backgroundImage: `linear-gradient(135deg, ${wash}, ${tone.accent}, ${wash})`,
    backgroundSize: '240% 100%',
    color: tone.ink,
    animation: 'freezeLivingFill 2800ms ease-in-out infinite alternate',
    '@keyframes freezeLivingFill': {
      from: { backgroundPosition: '0% 50%' },
      to: { backgroundPosition: '100% 50%' }
    },
    [REDUCED_MOTION]: { animation: 'none' }
  };
};

/** Frost grain that creeps across a pane. */
export const freezeFrostSx = () => ({
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    opacity: 0.25,
    backgroundImage: [
      'radial-gradient(rgba(215,242,255,0.5) 0.5px, transparent 0.5px)',
      'radial-gradient(rgba(215,242,255,0.35) 0.5px, transparent 0.5px)'
    ].join(','),
    backgroundSize: '26px 26px, 41px 41px',
    backgroundPosition: '0 0, 13px 20px',
    animation: 'freezeFrost 9000ms ease-in-out infinite alternate'
  },
  '@keyframes freezeFrost': {
    from: { backgroundPosition: '0 0, 13px 20px', opacity: 0.25 },
    to: { backgroundPosition: '140px 70px, 153px 90px', opacity: 0.5 }
  },
  [REDUCED_MOTION]: { '&::before': { animation: 'none' } }
});

/** Spring entrance for a ring or orb, which should arrive turning rather than fading. */
export const freezeOrbEnterSx = () => ({
  animation: 'freezeOrbEnter 1400ms cubic-bezier(0.34, 1.4, 0.64, 1) 200ms backwards',
  '@keyframes freezeOrbEnter': {
    from: { transform: 'scale(0.6) rotate(-90deg)', opacity: 0 },
    to: { transform: 'none', opacity: 1 }
  },
  [REDUCED_MOTION]: { animation: 'none' }
});

/** Hover on anything that should feel liftable. */
export const freezeLiftSx = {
  transition: 'transform 180ms cubic-bezier(0.16, 1, 0.3, 1), filter 180ms ease',
  '&:hover': { transform: 'translateY(-2px) scale(1.03)', filter: 'brightness(1.08)' },
  '&:active': { transform: 'translateY(0) scale(1)' },
  [REDUCED_MOTION]: { transition: 'none', '&:hover': { transform: 'none' } }
} as const;
